import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { CheckoutModal } from 'components/entitlements/CheckoutModal';
import { useEntitlements, usePlan, useTokenUsage, entitlementsApi } from 'modules/entitlements';
import type { Plan } from 'modules/entitlements';
import { useChatTiers } from 'modules/llm';
import { GetWallet } from 'modules/coins/api';
import { BillingHero } from 'components/entitlements/billing/BillingHero';
import { CoinWalletCard } from 'components/entitlements/billing/CoinWalletCard';
import { ReferralCard } from 'components/entitlements/billing/ReferralCard';
import {
  ResourceUsageCard,
  AiTiersCard,
  TokenUsageByMemberCard,
} from 'components/entitlements/billing/UsageSection';
import { PlansGrid } from 'components/entitlements/billing/PlansGrid';
import { TransactionHistoryModal } from 'components/entitlements/billing/TransactionHistoryModal';
import { TopUpModal } from 'components/entitlements/billing/TopUpModal';
import { IS_IO } from 'lib/site';

/** Billing & subscription page — plan summary, coin wallet, plans, then usage and referrals. */
const Transactions: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const entitlements = useEntitlements();
  const { key: planKey, name: planName } = usePlan();
  const { tiers: chatTiers } = useChatTiers();

  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);

  // Returning from a coin top-up checkout (BillingReturn forwards ?topup=success).
  // The webhook credits the wallet server-side; refetch coins so the new balance
  // shows, and strip the flag so a refresh doesn't re-trigger. The strip must go
  // through react-router (not raw history.replaceState): this page renders inside
  // the settings dialog, whose ?settings= contract reads the router's cached
  // search params — a raw replaceState would leave a stale topup=success there
  // to be resurrected by the next section switch.
  const [searchParams, setSearchParams] = useSearchParams();
  const [topupReturned, setTopupReturned] = useState(false);
  useEffect(() => {
    if (searchParams.get('topup') !== 'success') return;
    setTopupReturned(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('topup');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    if (!topupReturned) return;
    queryClient.invalidateQueries({ queryKey: ['coins'] });
    // A slightly-late webhook: refetch once more shortly after. (Separate
    // effect: the strip above re-renders, and a shared effect's cleanup would
    // cancel this timer before it fired.)
    const timer = setTimeout(() => queryClient.invalidateQueries({ queryKey: ['coins'] }), 3000);
    return () => clearTimeout(timer);
  }, [topupReturned, queryClient]);

  const { data: resourceCounts, isLoading: countsLoading } = useQuery({
    queryKey: ['entitlements', 'resource-counts'],
    queryFn: async () => (await entitlementsApi.GetResourceCounts()).data,
  });
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['entitlements', 'plans'],
    queryFn: async () => (await entitlementsApi.GetPlans()).data,
  });
  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ['coins', 'wallet'],
    queryFn: async () => (await GetWallet()).data,
  });
  const { data: tokenUsage, isLoading: tokenUsageLoading } = useTokenUsage();

  const status = entitlements?.subscriptionStatus ?? 'none';
  const periodEnd = entitlements?.currentPeriodEnd;
  const features = entitlements?.features ?? {};
  const aiTokensFeature = features['ai_tokens_monthly'];

  // Resolve the plan the renewal pays: current plan if purchasable, otherwise
  // the cheapest purchasable plan. Coins auto-apply at checkout.
  const purchasable = (plans ?? []).filter((p) => p.monthlyPrice > 0);
  const currentPlanObj = purchasable.find((p) => p.key === planKey) ?? null;
  const renewTarget =
    currentPlanObj ?? [...purchasable].sort((a, b) => a.monthlyPrice - b.monthlyPrice)[0] ?? null;
  const renewalPrice = status === 'none' ? null : renewTarget?.monthlyPrice ?? null;

  return (
    // No page-level heading: this page is only hosted inside the settings
    // dialog, whose section heading (SettingsDialog.tsx) carries the
    // "Plan & usage" title and the line under it.
    <div className="flex flex-col gap-4">
      {/* Payment alert — surfaced first so a past-due account sees the problem before anything else */}
      {(status === 'past_due' || status === 'unpaid') && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-error-200 bg-error-50 px-4 py-3.5 dark:border-error-900/60 dark:bg-error-900/20"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-error-700 dark:text-error-400" aria-hidden />
          <div className="text-[13.5px]">
            <p className="font-semibold text-error-700 dark:text-error-400">
              {t('payment_issue_u2014', 'Payment issue —')}{' '}
              {status === 'past_due' ? t('past_due', 'past due') : t('unpaid', 'unpaid')}
            </p>
            <p className="mt-0.5 text-foreground/80">
              {status === 'past_due'
                ? t(
                    'you_have_a_7_day_grace_period_before_access_is_restricted',
                    'You have a 7-day grace period before access is restricted.',
                  )
                : t('unpaid_payment_outstanding', 'Your payment is outstanding. Pay to keep your plan active.')}
            </p>
          </div>
        </div>
      )}

      <BillingHero
        planName={planName}
        status={status}
        periodEnd={periodEnd}
        renewalPrice={renewalPrice}
        onViewHistory={() => setHistoryOpen(true)}
      />

      {/* Coins, top-ups and referrals are examing.uz only. */}
      {!IS_IO && (
        <CoinWalletCard
          balance={wallet?.balance ?? 0}
          balanceLoading={walletLoading}
          planName={planName}
          renewalPrice={renewalPrice}
          onTopUp={() => setTopupOpen(true)}
        />
      )}

      <PlansGrid
        plans={plans}
        currentPlanKey={planKey}
        loading={plansLoading}
        onCheckout={(plan) => setCheckoutPlan(plan)}
      />

      {/* Beyond the plan summary: live usage, the AI quality ladder, who spent
          the tokens, and referrals. */}
      <div className="mt-4 flex flex-col gap-4">
        <ResourceUsageCard
          counts={resourceCounts}
          loading={countsLoading}
          assessments={features['assessments']}
          members={features['members']}
          documents={features['documents']}
          aiTokens={aiTokensFeature}
        />
        <AiTiersCard tiers={chatTiers} />
        {aiTokensFeature?.enabled && (
          <TokenUsageByMemberCard tokenUsage={tokenUsage} loading={tokenUsageLoading} />
        )}
        {!IS_IO && <ReferralCard />}
      </div>

      <CheckoutModal open={checkoutPlan !== null} onClose={() => setCheckoutPlan(null)} plan={checkoutPlan} />
      <TransactionHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      {!IS_IO && <TopUpModal open={topupOpen} onClose={() => setTopupOpen(false)} />}
    </div>
  );
};

export default Transactions;
